"use client";

import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";

export type ColorTheme = {
  preset: string;
  primary: string;
  accent: string;
  sidebar: string;
  sidebarText: string;
};

type DarkMode = "light" | "dark";

const DEFAULT_THEME: ColorTheme = {
  preset: "teal",
  primary: "#1A7A6E",
  accent: "#2a8f9e",
  sidebar: "#18181B",
  sidebarText: "#A1A1AA",
};

// Brightened per-preset primary colours for dark mode (legible on dark backgrounds)
const DARK_PRIMARY: Record<string, string> = {
  teal:    "#4ecdc4",
  slate:   "#94a3b8",
  indigo:  "#818cf8",
  emerald: "#34d399",
  rose:    "#fb7185",
};

export function applyTheme(theme: ColorTheme) {
  const root = document.documentElement;
  const isDark = root.classList.contains("dark");
  const primary = isDark
    ? (DARK_PRIMARY[theme.preset] ?? theme.primary)
    : theme.primary;
  root.style.setProperty("--color-primary",      primary);
  root.style.setProperty("--color-accent",       theme.accent);
  root.style.setProperty("--color-sidebar",      theme.sidebar);
  root.style.setProperty("--color-sidebar-text", theme.sidebarText);
  // Cache to localStorage so the blocking script in layout.tsx can apply colours
  // before React hydrates on next page load, preventing the teal flash.
  try { localStorage.setItem("portalTheme", JSON.stringify(theme)); } catch {}
}

interface ThemeContextValue {
  colorTheme: ColorTheme;
  toggleDark: () => void;
  setColorTheme: (t: ColorTheme) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  colorTheme: DEFAULT_THEME,
  toggleDark: () => {},
  setColorTheme: () => {},
});

export function useTheme() {
  return useContext(ThemeContext);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [colorTheme, setColorThemeState] = useState<ColorTheme>(DEFAULT_THEME);
  const darkModeRef = useRef<DarkMode>("light");
  const colorThemeRef = useRef<ColorTheme>(DEFAULT_THEME);
  const [mounted, setMounted] = useState(false);

  useLayoutEffect(() => {
    darkModeRef.current = document.documentElement.classList.contains("dark") ? "dark" : "light";
    // Use cached theme from localStorage so we don't overwrite the blocking
    // script's correctly-applied colours with the DEFAULT_THEME (teal) on mount.
    try {
      const cached = localStorage.getItem("portalTheme");
      const t: ColorTheme = cached ? JSON.parse(cached) : DEFAULT_THEME;
      colorThemeRef.current = t;
      setColorThemeState(t);
      applyTheme(t);
    } catch {
      applyTheme(DEFAULT_THEME);
    }
    setMounted(true);
  }, []);

  // Keep ref in sync with state
  useEffect(() => { colorThemeRef.current = colorTheme; }, [colorTheme]);

  const setColorTheme = useCallback((t: ColorTheme) => {
    setColorThemeState(t);
    const sid = (() => { try { const s = localStorage.getItem("staycasa_session"); return s ? JSON.parse(s)?.staffId : null; } catch { return null; } })();
    const headers: HeadersInit = { "Content-Type": "application/json", ...(sid ? { "x-staff-id": String(sid) } : {}) };
    fetch("/api/preferences", { method: "PUT", headers, body: JSON.stringify({ key: "colorTheme", value: JSON.stringify(t) }) }).catch(() => {});
    colorThemeRef.current = t;
    applyTheme(t);
  }, []);

  useEffect(() => {
    const getSid = () => { try { const s = localStorage.getItem("staycasa_session"); return s ? JSON.parse(s)?.staffId : null; } catch { return null; } };
    const sid = getSid();
    const prefHeaders: HeadersInit = sid ? { "x-staff-id": String(sid) } : {};

    Promise.all([
      fetch("/api/settings/theme").then(r => r.json()).catch(() => null),
      sid ? fetch("/api/preferences?key=darkMode", { headers: prefHeaders }).then(r => r.json()).catch(() => null) : Promise.resolve(null),
      sid ? fetch("/api/preferences?key=colorTheme", { headers: prefHeaders }).then(r => r.json()).catch(() => null) : Promise.resolve(null),
    ]).then(([serverTheme, prefResp, colorThemePref]) => {
      const currentMode = darkModeRef.current;
      const serverMode: DarkMode = (prefResp?.value === "dark" || prefResp?.value === "light") ? prefResp.value : currentMode;

      if (serverMode !== currentMode) {
        darkModeRef.current = serverMode;
        localStorage.setItem("darkMode", serverMode);
        document.documentElement.classList.toggle("dark", serverMode === "dark");
        applyTheme(colorThemeRef.current);
      }

      const isValidTheme = (t: unknown): t is ColorTheme =>
        !!t && typeof t === "object" && "primary" in t && "sidebar" in t;

      const resolvedTheme = (() => {
        try {
          if (colorThemePref?.value) {
            const parsed = JSON.parse(colorThemePref.value);
            if (isValidTheme(parsed)) return parsed;
          }
        } catch {}
        return isValidTheme(serverTheme) ? serverTheme : null;
      })();
      if (resolvedTheme) {
        const changed = JSON.stringify(resolvedTheme) !== JSON.stringify(colorThemeRef.current);
        if (changed) {
          setColorThemeState(resolvedTheme);
          colorThemeRef.current = resolvedTheme;
          applyTheme(resolvedTheme);
        }
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleDark = useCallback(() => {
    const next: DarkMode = darkModeRef.current === "dark" ? "light" : "dark";
    darkModeRef.current = next;
    // Suppress all CSS transitions for this frame so elements snap instantly
    const html = document.documentElement;
    html.classList.add("no-transition");
    html.classList.toggle("dark", next === "dark");
    applyTheme(colorThemeRef.current);
    // Re-enable transitions after the browser has painted the new theme
    requestAnimationFrame(() => html.classList.remove("no-transition"));
    localStorage.setItem("darkMode", next);
    // No setDarkMode — no React re-render, no context cascade, no lag.
    const sid = (() => { try { const s = localStorage.getItem("staycasa_session"); return s ? JSON.parse(s)?.staffId : null; } catch { return null; } })();
    const headers: HeadersInit = { "Content-Type": "application/json", ...(sid ? { "x-staff-id": String(sid) } : {}) };
    fetch("/api/preferences", { method: "PUT", headers, body: JSON.stringify({ key: "darkMode", value: next }) }).catch(() => {});
  }, []);

  if (!mounted) return null;

  return (
    <ThemeContext.Provider value={{ colorTheme, toggleDark, setColorTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

